import { Box, Button, Typography } from '@mui/material'
import { AspectRatio } from '@radix-ui/react-aspect-ratio'
import Image from 'next/image'
import { useRouter } from 'next/navigation'
import { FC, useCallback, useMemo, useState } from 'react'
import { Trans } from 'react-i18next'
import * as GA from '~/libs/ga'
import { Colors } from '~/libs/mui/theme'
import { getVideoWatchedPercent } from '~/libs/video/getVideoWatchedPercent'
import { useVideoDetail } from '../../hooks/useVideoDetail'
import { useVideoEventHandler } from '../VideoEventHandlerProvider'
import CountdownLabel from './CountdownLabel'

type VideoState = 'playing' | 'ended'

export const NextVideo: FC<{ id: string }> = ({ id }) => {
  const router = useRouter()
  const [didCancel, setDidCancel] = useState(false)
  const [videoState, setVideoState] = useState<VideoState>('playing')
  useVideoEventHandler({
    onEnded(event) {
      const video = event.target
      if (video instanceof HTMLVideoElement && video.duration > 10) {
        // ロードされる前のplaceholder動画のendedが呼ばれるので無視
        setVideoState('ended')
      }
    },
  })
  const { data } = useVideoDetail(id)
  const nextVideo = data?.originalVideo?.nextVideo

  const watchedPercent = useMemo(() => {
    const viewingTime = nextVideo?.viewingTime
    const duration = nextVideo?.duration
    if (!!viewingTime && !!duration) {
      return getVideoWatchedPercent(
        { minutes: duration.minutes ?? 0, seconds: duration.seconds ?? 0 },
        {
          minutes: viewingTime.viewingMin ?? 0,
          seconds: viewingTime.viewingSecond ?? 0,
        },
      )
    }
  }, [nextVideo])

  const onWatchNow = useCallback(() => {
    GA.sendEvent({
      event: 'click',
      screen: 'after_video',
      category: 'event',
      action: 'after_video_now',
      label: `${data?.originalVideo?.id}`,
      value: `${nextVideo?.id}`,
    })
    router.push(`/videos/${nextVideo?.id}/play`)
    setVideoState('playing')
  }, [data?.originalVideo?.id, nextVideo?.id, router])

  const onTimeup = useCallback(() => {
    GA.sendEvent({
      event: 'click',
      screen: 'after_video',
      category: 'event',
      action: 'after_video_auto_play',
      label: `${data?.originalVideo?.id}`,
      value: `${nextVideo?.id}`,
    })
    onWatchNow()
  }, [data?.originalVideo?.id, nextVideo?.id, onWatchNow])

  const onCancel = useCallback(() => {
    GA.sendEvent({
      event: 'click',
      screen: 'after_video',
      category: 'event',
      action: 'after_video_cancel',
      label: `${data?.originalVideo?.id}`,
      value: `${nextVideo?.id}`,
    })
    setDidCancel(true)
  }, [data?.originalVideo?.id, nextVideo?.id])

  // 『次のおすすめ』動画が95%以上視聴済みの場合は、表示をしない
  if (watchedPercent && watchedPercent >= 0.95) {
    return null
  }

  if (videoState == 'playing' || didCancel) {
    return <></>
  }

  if (!!nextVideo) {
    return (
      <Box
        position='absolute'
        top={0}
        left={0}
        width='100%'
        height='100%'
        sx={{ backgroundColor: Colors.color121213 }}
        padding={{ xs: 2, sm: 4 }}
      >
        <Box
          display='flex'
          flexDirection='column'
          width='100%'
          height='100%'
          gap={{ xs: 2, sm: 4 }}
          justifyContent='center'
        >
          <Box>
            <CountdownLabel onTimeup={onTimeup} />
          </Box>

          <Box display='flex'>
            <Box width='50%' paddingRight={2}>
              <AspectRatio ratio={163 / 92}>
                <Image
                  src={nextVideo?.landscapeThumbnail ?? ''}
                  alt={nextVideo?.title ?? ''}
                  fill
                  sizes='100vw'
                  style={{
                    objectFit: 'cover',
                    pointerEvents: 'none',
                    userSelect: 'none',
                    borderRadius: '8px',
                  }}
                />
              </AspectRatio>
            </Box>
            <Box width='50%' paddingLeft={2} overflow='hidden'>
              <AspectRatio ratio={163 / 92}>
                <Typography variant='h1' color={Colors.colorD7D9DB}>
                  {nextVideo.title}
                </Typography>
                <Typography variant='body2' color={Colors.colorD7D9DB}>
                  {nextVideo.description}
                </Typography>
              </AspectRatio>
            </Box>
          </Box>

          <Box display='flex' justifyContent='space-between' gap={{ xs: 2, sm: 4 }}>
            <Button
              variant='contained'
              sx={{
                flex: 1,
                backgroundColor: Colors.color23221F,
                color: Colors.colorD7D9DB,
                minHeight: 40,
                height: 40,
              }}
              onClick={onCancel}
            >
              <Trans i18nKey='cancel' />
            </Button>
            <Button
              variant='contained'
              sx={{ flex: 1, minHeight: 40, height: 40 }}
              onClick={onWatchNow}
            >
              <Trans i18nKey='watch_now' />
            </Button>
          </Box>
        </Box>
      </Box>
    )
  }

  return <></>
}
